import { useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useProducts from 'hooks/useProducts';
import { ParsedItem } from 'store/declaration-form-errors/ParsedForm';
import { getDeclarationUrl } from 'views/declarations/utils/getDeclarationUrl';
import { goToDeclarationField } from 'views/declarations/utils/form-utils';
import getErrorItemProductId from './getErrorItemProductId';

/** Long enough for the destination form to mount before we look for the field in it. */
const FIELD_LOOKUP_DELAY_MS = 100;

/**
 * Takes the reader to the field an error is about, for the declaration types whose form is a
 * master and a flat list of items - everything except ICS2, which has house levels of its own and
 * its own navigator.
 *
 * The item an error names is found by the id the error carries. The number the parsers record is
 * a position in the stored items, which the products list - one sorted page of them - does not
 * share. `getDeclarationUrl` is used rather than a hardcoded
 * `/declarations/...` so that a declaration opened from a manifest stays inside the manifest.
 */
const useGoToItemErrorField = (onNavigate?: () => void) => {
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const { declarationId, manifestId, jobId } = useParams();
    const { products } = useProducts();

    return useCallback(
        (item?: ParsedItem, fieldPath?: string) => {
            // Whatever the click came from covers the form, so it steps aside either way - a click
            // is never swallowed behind a panel that stays open.
            onNavigate?.();

            const productId = getErrorItemProductId(item, products?.list as { id?: string }[] | undefined);

            // An error naming an item that has since been deleted has nowhere to go.
            if (item?.index && !productId) return;

            const basePath = getDeclarationUrl({ manifestId, jobId, declarationId });
            const viewOnly = routerLocation.pathname.includes('view-only');

            navigate(`${basePath}${viewOnly ? '/view-only' : ''}${productId ? `/products/${productId}` : ''}`);

            if (fieldPath) goToDeclarationField(fieldPath, FIELD_LOOKUP_DELAY_MS);
        },
        [products, manifestId, jobId, declarationId, routerLocation.pathname, navigate, onNavigate]
    );
};

export default useGoToItemErrorField;
